<script lang="ts">
  /**
   * Скарга на коментар — шторка знизу на три кроки:
   *   reasons → confirm → done
   *
   * Крок підтвердження стоїть саме ПІСЛЯ вибору причини, а не замість нього:
   * список причин — це вузькі рядки один під одним, і промах пальцем по
   * сусідньому пункту коштував би чужому коментарю неправдивої скарги, яку вже
   * не відкликати (повторно поскаржитись не можна за побудовою).
   *
   * Каркас (backdrop + sheet + Esc) свідомо повторює ConfirmModal, щоб модалки
   * в застосунку виглядали й поводились однаково.
   */
  import { onMount } from 'svelte';
  import Icon from './Icon.svelte';
  import { COMMENT_REPORT_LABELS, COMMENT_REPORT_REASONS } from '../lib/types';
  import type { CommentReportReason } from '../lib/types';

  interface Props {
    open: boolean;
    /** Запит у польоті — блокуємо кнопки, щоб не надіслати двічі. */
    submitting?: boolean;
    /** Батько підняв прапорець після успішної відправки → показуємо «Дякуємо». */
    submitted?: boolean;
    /** Текст помилки з батька: показуємо в шторці, а не за нею. */
    errorText?: string | null;
    onSubmit: (reason: CommentReportReason) => void;
    onCancel: () => void;
  }
  let {
    open,
    submitting = false,
    submitted = false,
    errorText = null,
    onSubmit,
    onCancel,
  }: Props = $props();

  let selected = $state<CommentReportReason | null>(null);

  // Кожне відкриття починається з чистого списку — інакше шторка відкрилась би
  // одразу на кроці підтвердження причини, обраної минулого разу.
  $effect(() => {
    if (open) selected = null;
  });

  const step = $derived(submitted ? 'done' : selected ? 'confirm' : 'reasons');

  onMount(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || !open) return;
      // Esc на кроці підтвердження повертає до списку, а не закриває все:
      // людина ще не відмовилась від скарги, вона передумала щодо причини.
      if (step === 'confirm') selected = null;
      else onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
</script>

{#if open}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="rp-backdrop" aria-hidden="true" onclick={onCancel}></div>
  <div class="rp-sheet" role="dialog" aria-modal="true" aria-label="Скарга">
    <div class="rp-head">
      {#if step === 'confirm'}
        <button type="button" class="rp-back" onclick={() => (selected = null)} aria-label="Назад">
          <Icon name="arrow-left" size="xs" />
        </button>
      {/if}
      <span class="rp-head-title">Скарга</span>
      <button type="button" class="rp-close" onclick={onCancel} aria-label="Закрити">
        <Icon name="x" />
      </button>
    </div>

    {#if step === 'reasons'}
      <div class="rp-intro">
        <div class="rp-title">Чому ви скаржитеся на цей коментар?</div>
        <div class="rp-sub">Автор коментаря не побачить, що ви поскаржилися.</div>
      </div>

      <div class="rp-list">
        {#each COMMENT_REPORT_REASONS as reason (reason)}
          <button type="button" class="rp-item" onclick={() => (selected = reason)}>
            <span>{COMMENT_REPORT_LABELS[reason]}</span>
            <Icon name="chevron-right" size="xs" />
          </button>
        {/each}
      </div>
    {:else if step === 'confirm'}
      <div class="rp-intro">
        <div class="rp-title">Надіслати скаргу?</div>
        <div class="rp-sub">Причина, яку побачить адміністратор:</div>
        <div class="rp-reason">{COMMENT_REPORT_LABELS[selected!]}</div>
        <div class="rp-note">Поскаржитися на цей коментар повторно не вийде.</div>
      </div>

      {#if errorText}
        <div class="rp-error">{errorText}</div>
      {/if}

      <div class="rp-actions">
        <button type="button" class="rp-btn rp-btn-secondary" disabled={submitting} onclick={() => (selected = null)}>
          Змінити причину
        </button>
        <button type="button" class="rp-btn rp-btn-primary" disabled={submitting} onclick={() => onSubmit(selected!)}>
          {submitting ? 'Надсилаємо…' : 'Поскаржитися'}
        </button>
      </div>
    {:else}
      <div class="rp-intro">
        <div class="rp-done-icon"><Icon name="check" size="xl" /></div>
        <div class="rp-title">Дякуємо, що повідомили</div>
        <div class="rp-sub">Адміністратор перегляне цей коментар.</div>
      </div>

      <div class="rp-actions">
        <button type="button" class="rp-btn rp-btn-primary" onclick={onCancel}>Готово</button>
      </div>
    {/if}
  </div>
{/if}

<style>
  .rp-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9000;
  }
  /* Шторка знизу, а не картка по центру: список причин довгий, і на невисоких
     екранах центрована модалка впиралась би у краї. */
  .rp-sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9001;
    background: var(--bg);
    border-radius: 18px 18px 0 0;
    box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.3);
    max-height: 82vh;
    display: flex;
    flex-direction: column;
    padding-bottom: env(safe-area-inset-bottom);
  }
  .rp-head {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 14px 16px;
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
  }
  .rp-head-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
  }
  .rp-back {
    position: absolute;
    left: 12px;
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: var(--text-muted);
    display: inline-flex;
  }
  .rp-close {
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: var(--text-muted);
    display: inline-flex;
  }
  .rp-intro {
    padding: 18px 20px 14px;
    text-align: center;
    flex-shrink: 0;
  }
  .rp-title {
    font-size: 17px;
    font-weight: 700;
    color: var(--text);
    line-height: 1.3;
  }
  .rp-sub {
    margin-top: 8px;
    font-size: 12px;
    color: var(--text-muted);
    line-height: 1.45;
  }
  .rp-reason {
    margin-top: 10px;
    display: inline-block;
    background: var(--sand-soft);
    border-radius: 999px;
    padding: 7px 14px;
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
  }
  .rp-note {
    margin-top: 12px;
    font-size: 11px;
    color: var(--faint);
    line-height: 1.45;
  }
  .rp-done-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: var(--green-light);
    color: var(--green);
    margin-bottom: 12px;
  }
  .rp-error {
    margin: 0 20px 4px;
    font-size: 12px;
    color: var(--status-inactive);
    text-align: center;
  }
  .rp-list {
    overflow-y: auto;
    border-top: 1px solid var(--border);
  }
  .rp-item {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 15px 20px;
    background: none;
    border: none;
    border-bottom: 1px solid var(--border);
    font-family: inherit;
    font-size: 14px;
    text-align: left;
    color: var(--text);
    cursor: pointer;
  }
  .rp-item:last-child {
    border-bottom: none;
  }
  .rp-item:active {
    background: var(--sand-soft);
  }
  .rp-actions {
    display: flex;
    gap: 8px;
    padding: 8px 20px 20px;
  }
  .rp-btn {
    flex: 1;
    padding: 12px;
    border: none;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    transition: filter 0.15s, background 0.15s;
  }
  .rp-btn:disabled {
    opacity: 0.55;
    cursor: default;
  }
  .rp-btn-secondary {
    background: var(--bg2);
    color: var(--text);
    border: 1px solid var(--border);
  }
  .rp-btn-primary {
    background: linear-gradient(135deg, var(--pu), var(--pu3));
    color: var(--text-light);
  }
  .rp-btn-primary:not(:disabled):active {
    filter: brightness(1.05);
  }
</style>
